import React, { useEffect, useMemo } from "react";

type TabProps = {
  onChange: (value: string) => void;
  value: string;
  activeValue: string;
  label: React.ReactNode | string;
  classes?: Partial<{
    container: string;
    active: string;
    inactive: string;
  }>;
};

const Tab = ({
  onChange,
  value,
  label,
  activeValue,
  classes = {},
}: TabProps): React.ReactElement => (
  <button
    onClick={() => onChange(value)}
    className={[
      classes?.container ?? "",
      activeValue === value ? classes?.active ?? "" : classes?.inactive ?? "",
    ].join(" ")}
  >
    {label}
  </button>
);

type TabsProps = {
  onChange: (value: string) => void;
  children: (tabs: Array<React.ReactElement>) => React.ReactElement;
  defaultTab: string;
  tabs: Array<{
    value: string;
    label: React.ReactNode | string;
    disabled?: boolean;
  }>;
  classes?: Partial<{
    tab: TabProps["classes"];
  }>;
};

export const Tabs = ({
  onChange,
  children,
  defaultTab,
  tabs,
  classes = {},
}: TabsProps): React.ReactElement => {
  const [value, setValue] = React.useState<string>(defaultTab);
  const renderedTabs = useMemo(() => {
    const filteredTabs = tabs.filter(({ disabled }) => !disabled);

    return filteredTabs
      .map((tab, key) => (
        <Tab
          key={key}
          value={tab.value}
          activeValue={value}
          onChange={setValue}
          label={tab.label}
          classes={classes?.tab}
        />
      ))
      .reduce(
        (prev, tab, key) => [
          ...prev,
          tab,
          ...(key === filteredTabs.length - 1
            ? []
            : [
                <span
                  key={`${key}-${key}`}
                  className="h-[8px] w-[1px] border-l-[1px] border-solid border-[#9E9E9E]"
                />,
              ]),
        ],
        [] as Array<React.ReactElement>
      );
  }, [classes?.tab, tabs, value]);

  useEffect(() => {
    onChange(value);
  }, [value, onChange]);

  return <React.Fragment>{children(renderedTabs)}</React.Fragment>;
};
